<template>
    <b-sidebar
        id="destination-form-sidebar"
        :visible="isDestinationFormSidebarActive"
        bg-variant="white"
        sidebar-class="sidebar-lg"
        shadow
        backdrop
        no-header
        right
        @shown="getData(destinationData)"
        @hidden="resetForm"
        @change="(val) => $emit('update:is-destination-form-sidebar-active', val)"
    >
      <template #default="{ hide }">
        <div
          class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
        >
          <h5 class="mb-0"> Destination Form </h5>
          <feather-icon
            class="ml-1 cursor-pointer"
            icon="XIcon"
            size="16"
            @click="close(hide)"
          />
        </div>
        <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
            <b-form
                class="p-2"
                @submit.prevent="handleSubmit(onSubmit)"
                @reset.prevent="resetForm"
            >
                <b-tabs content-class="mt-1" v-if="langs.length > 0">
                    <b-tab v-for="(lang,key) in langs" :key="'tab-'+key"  :title="lang.name">
                        <validation-provider
                            #default="validationContext"
                            :name="`destination_name_${lang.code}`"
                            rules="min:0"
                        >
                            <b-form-group 
                                label="Destination Name" 
                                :label-for="`destination_name_${lang.code}`"
                            >
                                <quill-editor
                                    :options="editorOptions"
                                    :id="'description_' + lang.code"
                                    v-model="Data.name[lang.code]"
                                />
                                <b-form-invalid-feedback>
                                    {{ validationContext.errors[0] }}
                                </b-form-invalid-feedback>
                            </b-form-group>
                        </validation-provider>  
                    </b-tab>
                </b-tabs>
                <hr/>
                <validation-provider
                    #default="validationContext"
                    :name="`slug`"
                    rules="required"
                >
                    <b-form-group 
                        label="Slug" 
                        :label-for="`slug`"
                    >
                        <b-form-input
                            :id="`slug`"
                            v-model="Data.slug"
                            autofocus
                            :state="getValidationState(validationContext)"
                            trim
                            :placeholder="`slug`"
                            @input="changeSlug(Data.slug)"
                        />
                        <b-form-invalid-feedback>
                            {{ validationContext.errors[0] }}
                        </b-form-invalid-feedback>
                    </b-form-group>
                </validation-provider>
                <validation-provider
                    #default="validationContext"
                    name="IsActive"
                    rules="required"
                >
                    <b-form-group label="Is Active" label-for="IsActive">
                        <b-form-checkbox
                            id="isactive"
                            checked="true"
                            class="custom-control-success"
                            v-model="Data.is_active"
                            switch
                        />
                        <b-form-invalid-feedback>
                            {{ validationContext.errors[0] }}
                        </b-form-invalid-feedback>
                    </b-form-group>
                </validation-provider>
                <div class="d-flex mt-2">
                <b-button
                    v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                    variant="outline-success"
                    class="mr-2"
                    type="submit"
                >
                    Save
                </b-button>
                <b-button
                    v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                    type="button"
                    variant="outline-danger"
                    @click="close(hide)"
                >
                    Cancel
                </b-button>
                </div>
            </b-form>
        </validation-observer>
      </template>
    </b-sidebar>
  </template>
  
  <script>
    import { $themeConfig } from '@themeConfig'
    import {
      BSidebar, BForm, BFormGroup, BFormInput, BFormCheckbox, BFormInvalidFeedback, BButton,BTabs,BTab
    } from 'bootstrap-vue'
    import { ValidationProvider, ValidationObserver } from 'vee-validate'
    import { ref,onUnmounted } from '@vue/composition-api'
    import Ripple from 'vue-ripple-directive'
    import formValidation from '@core/comp-functions/forms/form-validation'
    import store from '@/store'
    import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
    import { useToast } from 'vue-toastification/composition'
    import { required, alphaNum, email, password, confirmed } from "@validations";
    import { slugify } from '@/@fake-db/utils'
    import { quillEditor } from "vue-quill-editor";
    import Quill from 'quill'
    const sizeStyle = Quill.import('attributors/style/size')
    sizeStyle.whitelist = ['6px', '8px', '10px', '12px', '14px', '16px', '18px', '20px', '24px', '30px', '32px', '36px']
    Quill.register(sizeStyle, true)
    import "quill/dist/quill.snow.css";
    import "quill/dist/quill.core.css";
    import "quill/dist/quill.bubble.css";
    import languagesStoreModule from '@/views/languages/languagesStoreModule'
  
    export default {
  
        components: {
            BSidebar,
            BForm,
            BFormGroup,
            BFormInput,
            BFormCheckbox,
            BFormInvalidFeedback,
            BButton,
            BTabs,BTab,
            ValidationProvider,
            ValidationObserver,
            quillEditor,
        },
  
        directives: {
            Ripple,
        },
  
        model: {
            prop: 'isDestinationFormSidebarActive',
            event: 'update:is-destination-form-sidebar-active',
        },

        computed: {
            editorOptions (){
                return $themeConfig.snowOption
            }
        },

        props: {
            isDestinationFormSidebarActive: {
                type: Boolean,
                required: true,
            },
            destinationData: {
                type: Object,
                required: false,
            },
        },
  
      setup(props, { emit }) {
        const {  appRef } = $themeConfig.app

        const LANGUAGES_STORE_MODULE_NAME = "app-languages";
        if (!store.hasModule(LANGUAGES_STORE_MODULE_NAME))
        store.registerModule(LANGUAGES_STORE_MODULE_NAME, languagesStoreModule);
        onUnmounted(() => {
            if (store.hasModule(LANGUAGES_STORE_MODULE_NAME))
            store.unregisterModule(LANGUAGES_STORE_MODULE_NAME);
        });

        const toast = useToast()
        const langs = ref([]);
        const userStorage = JSON.parse(localStorage.getItem('userData'));
  
        const blankData = {
            name: {},
            slug: '',
            is_active: true,
            created_by: userStorage.id,
        }
  
        const Data = ref(blankData)
        const oldValue  =  ref(null)
        const tabIndex = ref(0);
  
        const resetData = () => {
            Data.value = blankData;
        }
  
        const autoInputSlug = () => {
          Data.value.slug =  slugify(Data.value.name)
        }
  
        const getData = (data)=>{
            tabIndex.value = 0;
            Data.value.name = {}
            Data.value.slug = ''
            Data.value.is_active = true

            if(data){
                data.is_active = data.is_active?true:false;

                Data.value  = data;
                oldValue.value = JSON.parse(JSON.stringify({...data}))
            }
        }
  
        const onSubmit = () => {
            Data.value.is_active = Data.value.is_active?1:0;
  
            if(Data.value.id) {
                store.dispatch("app-sites/updateDestination", Data.value)
                .then((res) => {
                    toast({
                    component: ToastificationContent,
                    position: "top-right",
                    props: {
                        title: `Success`,
                        icon: "BellIcon",
                        variant: "success",
                        text: `${res.data.message}`,
                    }
                    });
                    emit("refetch-data");
                    emit("update:is-destination-form-sidebar-active", false);
                }).catch((error) => {
                    toast({
                    component: ToastificationContent,
                    position: "top-right",
                    props: {
                        title: `Error`,
                        icon: "BellIcon",
                        variant: "danger",
                        text: error.response.data.message,
                    },
                    });
                }
                );
            } else {
                store.dispatch("app-sites/addDestination", Data.value)
                    .then((res) => {
                        toast({
                            component: ToastificationContent,
                            position: "top-right",
                            props: {
                                title: `Success`,
                                icon: "BellIcon",
                                variant: "success",
                                text: `${res.data.message}`,
                            },
                        });
        
                        emit("refetch-data");
                        emit("update:is-destination-form-sidebar-active", false);
                    }).catch((error) => {
                        toast({
                            component: ToastificationContent,
                            position: "top-right",
                            props: {
                                title: `Error`,
                                icon: "BellIcon",
                                variant: "danger",
                                text: error.response.data.message,
                            },
                        });
                    }
                );
            }
        }

        const getLangs = (filter)=>{
            store.dispatch('app-languages/fetchData', filter)
                .then((res) => {
                    if(res.data.success){
                        langs.value = res.data.data;
                    }  
            });
        }

        let filter = {
            is_active:1,
        }

        getLangs(filter);

        const refeshList = async () => {
            emit("refetch-data");
        };

        const close = (hidden) => {
            for(const lang in oldValue.value?.name ?? {}) {
                Data.value.name[lang] = oldValue.value.name[lang]
            }

            hidden()
        }

        // const changeSlug  = (lang) => {
        //     if(lang === 'en'){
        //         console.log('value',Data.value.name[lang])
        //         Data.value.name[lang] = slugify(Data.value.slug);
        //     }
        // }

        const changeSlug = (slug) => {
            Data.value.slug = slugify(slug)
        }
  
        const {
          refFormObserver,
          getValidationState,
          resetForm,
        } = formValidation(resetData)
  
        return {
            refFormObserver,
            getValidationState,
            resetForm,
            resetData,
            getData,
            tabIndex,
            Data,
            onSubmit,
            autoInputSlug,
            getLangs,
            refeshList,
            langs,
            oldValue,
            close,
        //   changeSlug,
            changeSlug,
        }
      }
    }
  </script>

<style lang="scss">
    @import "@core/scss/vue/libs/quill.scss";
</style>